import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../api/axios";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);

export default function WeeklyTimeGrid({
  onSlotSelect,
  refreshKey,
  START_HOUR,
  END_HOUR,
  SLOT_MINUTES,
}) {
  const [weekStart, setWeekStart] = useState(dayjs().startOf("isoWeek"));
  const [bookedSlotMap, setBookedSlotMap] = useState({});

  const today = dayjs(); // ✅ added

  const days = Array.from({ length: 7 }, (_, i) =>
    weekStart.clone().add(i, "day"),
  );

  const timeSlots = [];
  for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      timeSlots.push({ hour: h, minute: m });
    }
  }

  /* ================= FETCH BOOKINGS ================= */
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const start = weekStart.clone().startOf("day");
        const end = weekStart.clone().add(6, "day").endOf("day");

        const { data } = await api.get("/conference-room/bookings", {
          params: {
            start: start.format("YYYY-MM-DD 00:00:01"),
            end: end.format("YYYY-MM-DD 23:59:59"),
          },
        });

        const slotMap = {};

        data.data.forEach((booking) => {
          let cursor = dayjs(booking.start);
          const bookingEnd = dayjs(booking.end);

          while (cursor.isBefore(bookingEnd)) {
            const key = cursor.format("YYYY-MM-DD_HH:mm");

            slotMap[key] = {
              convenor: booking.convenor,
              purpose: booking.purpose,
              attendees: booking.attendees || null,
              start: booking.start,
              end: booking.end,
            };

            cursor = cursor.add(SLOT_MINUTES, "minute");
          }
        });

        setBookedSlotMap(slotMap);
      } catch (err) {
        console.error("Booking fetch failed", err);
      }
    };

    fetchBookings();
  }, [weekStart, refreshKey, SLOT_MINUTES]);

  return (
    <Box p={2}>
      {/* HEADER */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <IconButton onClick={() => setWeekStart((p) => p.subtract(1, "week"))}>
          <ArrowBackIcon />
        </IconButton>

        <Typography fontWeight={600}>
          {weekStart.format("DD MMM")} –{" "}
          {weekStart.add(6, "day").format("DD MMM YYYY")}
        </Typography>

        <IconButton onClick={() => setWeekStart((p) => p.add(1, "week"))}>
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `70px repeat(7, 1fr)`,
          gap: 0.6,
          p: 1,
          borderRadius: 3,
          background: "rgba(255,255,255,0.04)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* DAY HEADERS */}
        <Box />
        {days.map((d) => {
          const isToday = d.isSame(today, "day");

          return (
            <Box
              key={d.toString()}
              sx={{
                textAlign: "center",
                fontWeight: 600,
                fontSize: "0.72rem",
                color: "primary.light",
                letterSpacing: 0.4,
                py: 0.4,
                lineHeight: 1.1,

                borderLeft: isToday ? "2px solid #22c55e" : "none",
                borderRight: isToday ? "2px solid #22c55e" : "none",
              }}
            >
              <Box>{d.format("ddd")}</Box>
              <Box
                sx={{
                  fontSize: "0.65rem",
                  color: "rgba(255,255,255,0.6)",
                  fontWeight: 500,
                }}
              >
                {d.format("DD")}
              </Box>
            </Box>
          );
        })}

        {/* TIME ROWS */}
        {timeSlots.map((t) => (
          <React.Fragment key={`${t.hour}-${t.minute}`}>
            {/* TIME LABEL */}
            <Box
              sx={{
                fontSize: "0.65rem",
                color: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                pr: 0.5,
              }}
            >
              {dayjs().hour(t.hour).minute(t.minute).format("HH:mm")}
            </Box>

            {/* CELLS */}
            {days.map((d) => {
              const slotStart = d.clone().hour(t.hour).minute(t.minute);
              const slotKey = slotStart.format("YYYY-MM-DD_HH:mm");
              const booking = bookedSlotMap[slotKey];
              const isBooked = Boolean(booking);
              const isToday = d.isSame(today, "day");

              return (
                <Box
                  key={slotKey}
                  sx={{
                    height: 32,
                    borderRadius: 1.5,
                    px: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    whiteSpace: "nowrap",

                    cursor: isBooked ? "default" : "pointer",

                    background: isBooked
                      ? "linear-gradient(135deg, rgba(5,130,233,0.55), rgba(143,184,245,0.55))"
                      : "rgba(255,255,255,0.03)",

                    borderLeft: isToday ? "2px solid #22c55e" : "none",
                    borderRight: isToday ? "2px solid #22c55e" : "none",

                    boxShadow: isBooked
                      ? "inset 0 0 0 1px rgba(255,255,255,0.15)"
                      : "inset 0 0 0 1px rgba(255,255,255,0.06)",

                    transition: "all 0.15s ease-in-out",

                    "&:hover": {
                      background: isBooked
                        ? "linear-gradient(135deg, rgba(239,68,68,0.65), rgba(185,28,28,0.65))"
                        : "rgba(34,197,94,0.18)",
                      transform: isBooked ? "none" : "scale(1.04)",
                    },

                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                  onClick={() => {
                    if (isBooked) {
                      onSlotSelect({
                        start: slotStart,
                        booking,
                        isExisting: true,
                      });
                    } else {
                      onSlotSelect({
                        start: slotStart,
                        booking: null,
                        isExisting: false,
                      });
                    }
                  }}
                >
                  {isBooked && (
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 500,
                        color: "#fff",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        textAlign: "center",
                        textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                      }}
                      title={booking.convenor}
                    >
                      {booking.convenor}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
